import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { generatePayInvoiceUrl, encryptHotelId } from '../utils/payInvoiceUtils';

/**
 * Admin tool for generating payment links for specific hotels
 * This can be used by admin/staff to create payment links
 */
const PaymentLinkGenerator = () => {
  const [hotelId, setHotelId] = useState('');
  const [hotelName, setHotelName] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleGenerateLink = () => {
    if (!hotelId.trim()) {
      setError('Please enter a Hotel ID');
      return;
    }

    try {
      const url = generatePayInvoiceUrl(hotelId.trim());
      setGeneratedUrl(url);
      setError('');
      setCopied(false);
    } catch (err) {
      setError('Failed to generate link');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenLink = () => {
    window.open(generatedUrl, '_blank');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3, color: '#2E4057' }}>
        Payment Link Generator
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Generate secure payment links for specific hotels. These links allow hotel owners to pay their invoices 
        without logging into the extranet.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Hotel Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hotel ID"
                value={hotelId}
                onChange={(e) => setHotelId(e.target.value)}
                placeholder="Enter Hotel ID (e.g., 12345)"
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hotel Name (Optional)"
                value={hotelName}
                onChange={(e) => setHotelName(e.target.value)}
                placeholder="Enter Hotel Name"
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleGenerateLink}
              disabled={!hotelId.trim()}
              sx={{
                background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #E56900 0%, #FF7700 100%)',
                }
              }}
            >
              Generate Payment Link
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {generatedUrl && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Generated Payment Link
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 2, 
              mb: 2,
              wordBreak: 'break-all'
            }}>
              <Typography variant="body2" fontFamily="monospace">
                {generatedUrl}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                onClick={handleCopyLink}
                sx={{
                  background: copied ? '#4CAF50' : 'linear-gradient(90deg, #2196F3 0%, #1976D2 100%)',
                  '&:hover': {
                    background: copied ? '#45A049' : 'linear-gradient(90deg, #1976D2 0%, #1565C0 100%)',
                  }
                }}
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<OpenIcon />}
                onClick={handleOpenLink}
                sx={{
                  borderColor: '#FF7700',
                  color: '#FF7700',
                  '&:hover': {
                    background: 'rgba(255, 119, 0, 0.1)',
                    borderColor: '#E56900',
                  }
                }}
              >
                Test Link
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Instructions:</strong>
                <br />
                1. Copy this link and send it to the hotel owner
                <br />
                2. They can open it directly without logging in
                <br />
                3. The link is secure and cannot be tampered with
                <br />
                4. Only invoices for Hotel ID <strong>{hotelId}</strong> will be shown
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Example Usage */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Example Usage
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Here are some example hotel IDs and their corresponding encrypted tokens:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label="Hotel ID: 12345" size="small" />
              <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                {encryptHotelId('12345')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label="Hotel ID: 67890" size="small" />
              <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                {encryptHotelId('67890')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label="Hotel ID: 11111" size="small" />
              <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                {encryptHotelId('11111')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentLinkGenerator;

